import { List, ListItem, Text, VStack } from "@chakra-ui/react";
import { ReactNode } from "react";

export interface AccordionItem {
  title: string;
  content: ReactNode;
}

export interface HeroSection {
  title: string;
  subtitle: string;
  ctaButton: {
    text: string;
    href: string;
  };
  backgroundImage: string;
  heroImage: {
    src: string;
    alt: string;
  };
}

export interface AboutSection {
  title: string;
  description: ReactNode;
  image: {
    src: string;
    alt: string;
  };
  accordionItems: AccordionItem[];
}

export interface DocumentsSection {
  title: string;
  ctaButton: {
    text: string;
    href: string;
  };
  image: {
    src: string;
    alt: string;
  };
}

export interface LandingConfig {
  hero: HeroSection;
  about: AboutSection;
  documents: DocumentsSection;
}

export const landingConfig: LandingConfig = {
  hero: {
    title: "SISAN",
    subtitle:
      "Sistema de Informação e Alerta Rápido para a Segurança Alimentar e Nutricional",
    ctaButton: {
      text: "Entrar",
      href: "/login",
    },
    backgroundImage: "/images/geometric-forms-bg.png",
    heroImage: {
      src: "/images/woman-holding-tablet-front-view.png",
      alt: "Woman holding tablet",
    },
  },
  about: {
    title: "Sobre o SISAN",
    description: (
      <VStack gap="2">
        <Text>
          O Sistema de Informação e Alerta Rápido para a Segurança Alimentar e
          Nutricional (SISAN) é uma ferramenta crucial para a recolha,
          tratamento e análise de informações, contribuindo para a tomada de
          decisões e promovendo uma resposta multissectorial, de múltiplos
          actores e multinível à segurança alimentar e nutricional.
        </Text>
        <Text>
          O objectivo final deste sistema passa por permitir que as autoridades
          responsáveis consigam prever e/ou evitar a ocorrência de situações que
          condicionem a disponibilidade e/ou o acesso das populações mais
          vulneráveis a alimentação necessária à sua sobrevivência,
          principalmente através da monitorização de indicadores SAN em dois
          níveis de informação: provincial e municipal.
        </Text>
      </VStack>
    ),
    image: {
      src: "/images/mosaico 1.png",
      alt: "About SISAN",
    },
    accordionItems: [
      {
        title: "Em que contexto nasce?",
        content: (
          <VStack gap="2" align="flex-start">
            <Text>
              O SISAN enquadra-se na Estratégia Nacional de Segurança Alimentar
              e Nutricional de Angola II (ENSAN II), no âmbito da
              institucionalização e operacionalização do Sistema Nacional de
              Segurança Alimentar e Nutricional - SINASAN e tem como um dos seus
              objectivos específicos o de criar e implementar uma ferramenta
              informática de monitoria e avaliação da segurança alimentar e
              nutricional (o SISAN) a nível nacional, provincial, municipal, bem
              como mecanismos de alerta, de comunicação e informação às
              autoridades responsáveis e consequentemente às famílias.
            </Text>
            <Text>
              O SINASAN, reforçará a coordenação dos diferentes sectores
              implicados em matéria da SAN, irá garantir um ambiente de Leis e
              políticas favoráveis à SAN e aos sistemas alimentares sustentáveis
              e descentralizados, com uma formulação integrada de programas a
              todos os níveis. Garantirá também uma implementação mais eficiente
              das acções, o monitoramento efectivo e a avaliação dos diferentes
              planos e programas de todos os departamentos ministeriais,
              institutos, agências ou aquelas estruturas governamentais
              relevantes em matéria SAN.
            </Text>
            <Text>
              No quadro da ENSAN II, a produção e transmissão da informação dos
              sistemas alimentares será articulada no âmbito do estabelecimento
              de ferramentas como o SISAN, plataforma de informação e
              retro-informação da SAN que consiste em acompanhar os factores
              susceptíveis que afectam adversamente as condições de vida das
              populações, incluindo o processo de produção, vigilância
              nutricional, mercados e preços, através de um processo analítico
              coerente e a divulgação regular e oportuna das informações. Com
              isso poderá dotar a sociedade em geral e as autoridades
              governamentais competentes, em particular dos elementos
              necessários à tomada de decisões preventivas ou correctivas em
              matéria de SAN, e a medição dos impactos na SAN para a salvaguarda
              do bem-estar das populações.
            </Text>
          </VStack>
        ),
      },
      {
        title: "Para que serve?",
        content: (
          <VStack gap="2" align="flex-start">
            <Text>
              O SISAN desenvolve as seguintes acções nos diferentes níveis
              territoriais (central, provincial, municipal):
            </Text>
            <Text>
              <List pl={10} styleType="disc">
                <ListItem>Monitoria das actividades agrícolas</ListItem>
                <ListItem>Apoio na previsão das colheitas</ListItem>
                <ListItem>Factores agro-meteorológicos</ListItem>
                <ListItem>Monitoria dos preços no mercado</ListItem>
                <ListItem>
                  Análise de vulnerabilidade dos grupos populacionais
                </ListItem>
                <ListItem>Análises nutricionais</ListItem>
                <ListItem>Vigilância nutricional</ListItem>
                <ListItem>Disponibilidade de recursos hídricos</ListItem>
                <ListItem>Sistemas integrados de alerta rápido</ListItem>
              </List>
            </Text>
            <Text>
              O SISAN desenvolve um processo analítico para criar informações e
              alertas pertinentes para a tomada de decisões das CONSAN / COPSAN
              / COMSAM (Conselhos Nacional/Provincial/Municipal de Segurança
              Alimentar e Nutricional). O SISAN apresenta com regularidade os
              dados oficiais dos vários indicadores multissectoriais
              seleccionados, na sua plataforma, aos seus utilizadores.
            </Text>
            <Text>
              Embora o SISAN não seja responsável directamente pela garantia da
              segurança alimentar, o SISAN presta informações fundamentadas
              sobre situações locais de vulnerabilidade à insegurança alimentar
              e nutricional. Isso permite que autoridades responsáveis prevejam
              e evitem situações que comprometam a disponibilidade e o acesso
              das populações mais vulneráveis aos alimentos necessários para a
              sobrevivência.
            </Text>
          </VStack>
        ),
      },
      {
        title: "Quem utiliza?",
        content: (
          <VStack gap="2" align="flex-start">
            <Text>
              O SISAN presta informação fundamentada sobre as situações locais
              de vulnerabilidade em matérias de segurança alimentar e
              nutricional, às entidades governamentais e de gestão da SAN, para
              tomada de decisão.
            </Text>
          </VStack>
        ),
      },
    ],
  },
  documents: {
    title:
      "Encontre e descarregue\nrelatórios e outros\ndocumentos relevantes em\nmatérias de SAN",
    ctaButton: {
      text: "Ir para Repositório de Documentos",
      href: "/documentos",
    },
    image: {
      src: "/images/documents-image.png",
      alt: "Documents",
    },
  },
};
